import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Chip, Button, IconButton, Tooltip, Link} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';

const DailyBonusConfigsPage = () => {
    const navigate = useNavigate();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchConfigs = useCallback(() => {
        setLoading(true);
        apiService.get('/api/daily-bonus-configs')
            .then(data => setConfigs(data))
            .catch(err => setError(err.message || 'Failed to fetch daily bonus configs'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/daily-bonus-configs/${itemToDelete.dayNumber}`);
            fetchConfigs();
        } catch (err) {
            setError('Failed to delete daily bonus config.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'dayNumber',
            header: 'Day',
            size: 80,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/daily-bonus-configs/${cell.getValue()}`}>
                    Day {cell.getValue()}
                </Link>
            ),
        },
        {
            id: 'rewardSummary',
            header: 'Reward',
            size: 300,
            Cell: ({row}) => {
                const reward = row.original.reward;
                if (!reward?.length) return '-';
                return reward.map((r, i) => {
                    const c = r.criteria || {};
                    const amt = c.amount_min && c.amount_max
                        ? `${c.amount_min?.toLocaleString()}-${c.amount_max?.toLocaleString()}`
                        : (c.amount?.toLocaleString() ?? '?');
                    return `${r.entity} #${c.identifier}: ${amt}`;
                }).join(', ');
            },
        },
        {
            accessorKey: 'enabled',
            header: 'Enabled',
            size: 100,
            Cell: ({cell}) => (
                <Chip
                    label={cell.getValue() ? 'Yes' : 'No'}
                    color={cell.getValue() ? 'success' : 'default'}
                    size="small"
                />
            ),
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data: configs,
        enablePagination: false,
        enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {
            sorting: [{id: 'dayNumber', desc: false}],
        },
        renderTopToolbarCustomActions: () => (
            <Protected component={DailyBonusConfigsPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>}
                        onClick={() => navigate('/daily-bonus-configs/new')}>
                    Add Day
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink}
                                to={`/daily-bonus-configs/${row.original.dayNumber}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={DailyBonusConfigsPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => {
                            setItemToDelete(row.original);
                            setConfirmOpen(true);
                        }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Daily Bonus Config</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={DailyBonusConfigsPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDeleteItem}
                    title={`Delete Day ${itemToDelete?.dayNumber}?`}
                    message="Are you sure you want to delete this daily bonus config? This action cannot be undone."
                />
            </Protected>
        </Box>
    );
};

DailyBonusConfigsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default DailyBonusConfigsPage;
